import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { LedgerSummary, Company } from '../types/billing';

// Company information for header
const COMPANY_INFO = {
  name: "AVI'S ENGINEERING & CONSULTANCY",
  subtitle: 'NDT SERVICES',
  address: 'SR No-4/1A; plot on-: 17 SHREE SWAMI SAMARTH NIWAS, Bistabag, Savedi, Ahilyanagar',
  phone: '7887985350',
  email: 'avis.engineering2025@gmail.com',
  gstn: '27FLTPM3802F1ZU',
};

// ===================== Styles =====================
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 15,
    fontSize: 9,
    lineHeight: 1.2,
  },
  companyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
    paddingBottom: 6,
    borderBottomWidth: 2,
    borderBottomColor: '#2C4A6B',
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2C4A6B',
    marginBottom: 2,
  },
  companySubtitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#2C4A6B',
    marginBottom: 4,
  },
  companyDetails: {
    fontSize: 8,
    color: '#333333',
    lineHeight: 1.2,
  },
  ledgerHeader: {
    marginBottom: 12,
    marginTop: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2C4A6B',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 10,
    color: '#666666',
    marginBottom: 4,
  },
  clientCompanyName: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#333333',
    marginTop: 6,
  },
  summarySection: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: '#F5F5F5',
    borderRadius: 5,
  },
  summaryTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2C4A6B',
    marginBottom: 10,
  },
  summaryGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  summaryItem: {
    flex: 1,
    padding: 10,
    backgroundColor: '#FFFFFF',
    marginRight: 10,
    borderRadius: 3,
  },
  summaryItemLast: {
    flex: 1,
    padding: 10,
    backgroundColor: '#FFFFFF',
    borderRadius: 3,
  },
  summaryLabel: {
    fontSize: 9,
    color: '#666666',
    marginBottom: 5,
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333333',
  },
  totalBilled: {
    color: '#10B981',
  },
  totalReceived: {
    color: '#3B82F6',
  },
  outstanding: {
    color: '#F59E0B',
  },
  table: {
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#2C4A6B',
    padding: 8,
    borderRadius: 3,
  },
  tableHeaderText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
  },
  tableRowText: {
    fontSize: 9,
    color: '#333333',
  },
  tableFooter: {
    flexDirection: 'row',
    backgroundColor: '#F5F5F5',
    padding: 8,
    fontWeight: 'bold',
    borderTopWidth: 2,
    borderTopColor: '#2C4A6B',
  },
  tableFooterText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#333333',
  },
  colLedgerName: {
    width: '40%',
  },
  colDebit: {
    width: '30%',
    textAlign: 'right',
  },
  colCredit: {
    width: '30%',
    textAlign: 'right',
  },
  // Transaction table columns
  colDate: {
    width: '12%',
  },
  colParticulars: {
    width: '25%',
  },
  colVchType: {
    width: '12%',
  },
  colVchNo: {
    width: '18%',
  },
  colTransDebit: {
    width: '16%',
    textAlign: 'right',
  },
  colTransCredit: {
    width: '17%',
    textAlign: 'right',
  },
  tableRowBold: {
    flexDirection: 'row',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
    borderTopWidth: 2,
    borderTopColor: '#333333',
  },
  tableRowTextBold: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#333333',
  },
  footer: {
    marginTop: 30,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
    fontSize: 8,
    color: '#666666',
    textAlign: 'center',
  },
});

interface LedgerPDFProps {
  summary: LedgerSummary;
  company?: Company;
}

const LedgerPDF: React.FC<LedgerPDFProps> = ({ summary, company }) => {
  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '0';
    const rounded = Math.round(value);
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(rounded);
  };

  const totalDebit = summary.accounts.reduce((acc, a) => acc + (a.debitBalance || 0), 0);
  const totalCredit = summary.accounts.reduce((acc, a) => acc + (a.creditBalance || 0), 0);

  const totalBilled = summary.accounts.find(a => a.ledgerName.includes('Sales'))?.creditBalance || 0;
  const totalReceived = summary.accounts.find(a => a.ledgerName.includes('Cash/Bank'))?.debitBalance || 0;
  const outstanding = summary.accounts.find(a => a.ledgerName.includes('Outstanding'))?.debitBalance || 0;

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: '2-digit' 
    }).replace(/\s/g, '-');
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Company Header */}
        <View style={styles.companyHeader}>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>{COMPANY_INFO.name}</Text>
            <Text style={styles.companySubtitle}>{COMPANY_INFO.subtitle}</Text>
            <View style={styles.companyDetails}>
              <Text>{COMPANY_INFO.address}</Text>
              <Text>Ph: {COMPANY_INFO.phone} | Email: {COMPANY_INFO.email}</Text>
              <Text>GSTIN: {COMPANY_INFO.gstn}</Text>
            </View>
          </View>
        </View>

        {/* Ledger Header */}
        <View style={styles.ledgerHeader}>
          <Text style={styles.title}>Ledger Balance</Text>
          <Text style={styles.subtitle}>{summary.periodLabel}</Text>
          {company && (
            <Text style={styles.clientCompanyName}>Client: {company.name}</Text>
          )}
        </View>

        {/* Transaction Details Table */}
        {summary.transactions && summary.transactions.length > 0 && (
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderText, styles.colDate]}>Date</Text>
              <Text style={[styles.tableHeaderText, styles.colParticulars]}>Particulars</Text>
              <Text style={[styles.tableHeaderText, styles.colVchType]}>Vch Type</Text>
              <Text style={[styles.tableHeaderText, styles.colVchNo]}>Vch No.</Text>
              <Text style={[styles.tableHeaderText, styles.colTransDebit]}>Debit</Text>
              <Text style={[styles.tableHeaderText, styles.colTransCredit]}>Credit</Text>
            </View>

            {summary.transactions.map((trans, idx) => {
              const isBold = trans.isClosingBalance && trans.debit === trans.credit;
              const hasBorder = trans.isSubtotal || (trans.isClosingBalance && trans.debit === trans.credit);
              const rowStyle = hasBorder ? styles.tableRowBold : styles.tableRow;
              const textStyle = isBold ? styles.tableRowTextBold : styles.tableRowText;

              return (
                <View key={idx} style={rowStyle}>
                  <Text style={[textStyle, styles.colDate]}>{formatDate(trans.date)}</Text>
                  <Text style={[textStyle, styles.colParticulars]}>{trans.particulars}</Text>
                  <Text style={[textStyle, styles.colVchType]}>{trans.vchType || ''}</Text>
                  <Text style={[textStyle, styles.colVchNo]}>{trans.vchNo || ''}</Text>
                  <Text style={[textStyle, styles.colTransDebit]}>
                    {trans.debit !== undefined ? formatCurrency(trans.debit) : ''}
                  </Text>
                  <Text style={[textStyle, styles.colTransCredit]}>
                    {trans.credit !== undefined ? formatCurrency(trans.credit) : ''}
                  </Text>
                </View>
              );
            })}
          </View>
        )}

        {/* Summary Accounts Table */}
        {summary.accounts.length > 0 && (
          <View style={styles.summarySection}>
            <Text style={styles.summaryTitle}>Financial Summary</Text>
            <View style={styles.summaryGrid}>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Total Billed</Text>
                <Text style={[styles.summaryValue, styles.totalBilled]}>
                  {formatCurrency(totalBilled)}
                </Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Total Received</Text>
                <Text style={[styles.summaryValue, styles.totalReceived]}>
                  {formatCurrency(totalReceived)}
                </Text>
              </View>
              <View style={styles.summaryItemLast}>
                <Text style={styles.summaryLabel}>Outstanding</Text>
                <Text style={[styles.summaryValue, styles.outstanding]}>
                  {formatCurrency(outstanding)}
                </Text>
              </View>
            </View>
          </View>
        )}

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.colLedgerName]}>Ledger Name</Text>
            <Text style={[styles.tableHeaderText, styles.colDebit]}>Debit Balance</Text>
            <Text style={[styles.tableHeaderText, styles.colCredit]}>Credit Balance</Text>
          </View>

          {summary.accounts.length === 0 ? (
            <View style={styles.tableRow}>
              <Text style={[styles.tableRowText, styles.colLedgerName]}>
                No data available
              </Text>
            </View>
          ) : (
            summary.accounts.map((acc, idx) => (
              <View key={idx} style={styles.tableRow}>
                <Text style={[styles.tableRowText, styles.colLedgerName]}>
                  {acc.ledgerName}
                </Text>
                <Text style={[styles.tableRowText, styles.colDebit]}>
                  {formatCurrency(acc.debitBalance)}
                </Text>
                <Text style={[styles.tableRowText, styles.colCredit]}>
                  {formatCurrency(acc.creditBalance)}
                </Text>
              </View>
            ))
          )}

          {summary.accounts.length > 0 && (
            <View style={styles.tableFooter}>
              <Text style={[styles.tableFooterText, styles.colLedgerName]}>Totals</Text>
              <Text style={[styles.tableFooterText, styles.colDebit]}>
                {formatCurrency(totalDebit)}
              </Text>
              <Text style={[styles.tableFooterText, styles.colCredit]}>
                {formatCurrency(totalCredit)}
              </Text>
            </View>
          )}
        </View>

        <View style={styles.footer}>
          <Text>Generated on {new Date().toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default LedgerPDF;

